import React, { useEffect, useState, useMemo } from 'react';
import styles from '../insertsPages.module.css';
import Chooser from '../../../Chooser/index.jsx';
import Tabs from '../../../Tabs';
import { FileInput } from '../../../../../../personalisation/src/components/FileInput';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserImageArea, VisualiserTextBox, VisualiserGroup } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantForProduct } from '../../../../utilities/visualiserVariants.js';

const tabConfig = [
  { id: 'FRONT', label: 'Front' },
  { id: 'INSIDE', label: 'Inside' },
  { id: 'BACK', label: 'Back' }
];

/**
 * CardThemedPhotoUpload component allows users to upload a photo for their greeting card.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function CardThemedPhotoUpload({ ...rest }) {
  const {
    getImageInput,
    setImageInput,
    getTextInput,
    setTextInput,
    getTransformation,
    setTransformation,
    setVisualiserSelection,
    clearVisualiserSelection
  } = useUserInput();

  const [activeIndex, setActiveIndex] = useState(0);

  const frontImageKey = 'cardThemedPhotoUpload.frontImage';
  const frontImage = getImageInput(frontImageKey);
  const frontTransformation = getTransformation(frontImageKey);

  const themedProduct = useMemo(() => {
    return getInsertsAndCardsProduct({ productKey: 'themedGreetingsCards' });
  }, []);

  const imageVariants = useMemo(() => {
    if (!themedProduct || !Array.isArray(themedProduct.variants)) return [];
    return themedProduct.variants.filter((variant) => variant?.option1 === 'Image Upload');
  }, [themedProduct]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    imageVariants && imageVariants.length > 0 ? imageVariants[0].id : null
  );

  useEffect(() => {
    if (!imageVariants || imageVariants.length === 0) return;
    if (!selectedVariantId || !imageVariants.find((variant) => variant.id === selectedVariantId)) {
      setSelectedVariantId(imageVariants[0].id);
    }
  }, [imageVariants, selectedVariantId]);

  const selectedVariant = useMemo(
    () => imageVariants.find((variant) => variant.id === selectedVariantId) || null,
    [imageVariants, selectedVariantId]
  );

  const backgroundImageUrl = useMemo(() => {
    const raw = selectedVariant?.featured_image?.src || selectedVariant?.featured_media?.preview_image?.src || null;
    if (!raw || typeof raw !== 'string') return null;
    return raw.startsWith('/' + '/') ? `https:${raw}` : raw;
  }, [selectedVariant]);

  useEffect(() => {
    const productKey = 'themedGreetingsCards';
    setVisualiserSelection({
      productKey,
      variant: selectedVariant,
      config: {
        templateId: 'CARD_THEMED_PHOTO_UPLOAD',
        mapping: {
          text: {
            'cardThemedPhotoUpload.front': 'UVFrontText',
            'cardThemedPhotoUpload.inside': 'UVInsideText'
          },
          images: {
            'cardThemedPhotoUpload.frontImage': 'UVFrontImage'
          }
        }
      }
    });
  }, [selectedVariant, setVisualiserSelection]);

  useEffect(() => {
    return () => {
      clearVisualiserSelection();
    };
  }, [clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs tabs={tabConfig} activeIndex={activeIndex} onChange={setActiveIndex} fullWidth={true} />

        {activeIndex === 0 && (
          <Visualiser 
            type="GREETINGS_CARD" 
            background={backgroundImageUrl ? `url(${backgroundImageUrl})` : 'var(--color-white)'}
            subtitle="Upload an image below"
          >
            <VisualiserImageArea
              value={frontImage}
              size={{
                width: '165px',
                height: '165px'
              }}
              position={{
                top: '49px',
                left: '29px'
              }}
              borderRadius="0"
              backgroundColor="var(--color-white)"
              style={{ 
                border: '0.1rem solid rgba(0, 0, 0, 0.3)'
              }}
              alt="Greeting card photo upload preview"
              scale={frontTransformation.scale}
              x={frontTransformation.x}
              y={frontTransformation.y}
              onUpdate={(updates) => setTransformation(frontImageKey, updates)}
            />
            <VisualiserTextBox
              size={{
                width: '168px',
                minWidth: '168px',
                maxWidth: '168px',
                minHeight: '10px',
                maxHeight: '20px'
              }}
              position={{
                top: '215px',
                left: '28px'
              }}
              fontWeight="bold"
              maxLength={40}
              maxLines={1}
              font="Romie"
              fontSize={21}
              color={'#751521'}
              value={getTextInput('cardThemedPhotoUpload.front')}
              setValue={((value) => setTextInput('cardThemedPhotoUpload.front', value))}
            />            
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <VisualiserGroup>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
              {' '}
            </Visualiser>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
              <VisualiserTextBox
                size={{
                  width: '190px',
                  minWidth: '150px',
                  maxWidth: '190px',
                  minHeight: '50px',
                  maxHeight: '260px'
                }}
                position={{
                  top: '50%',
                  left: '50%'
                }}
                maxLength={150}
                maxLines={12}
                lineHeight={1.2}
                fontSize={16}
                style={{
                  transform: 'translate(-50%, -50%)'
                }}
                value={getTextInput('cardThemedPhotoUpload.inside')}
                setValue={((value) => setTextInput('cardThemedPhotoUpload.inside', value))}
              />
            </Visualiser>
          </VisualiserGroup>
        )}

        {activeIndex === 2 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
            {' '}
          </Visualiser>
        )}

        {activeIndex === 0 && (
          <div className={styles['file-input-container']}>
            <FileInput
              label="Upload a Photo"
              onFileSelect={(value) => setImageInput(frontImageKey, value)}
              accept={'png,jpg,jpeg,webp'}
              disabled={false}
              buttonVariant="primary"
              buttonColor="orange"
              buttonSize="small"
              maxFileSize={5 * 1024 * 1024}
              id="front-image"
              selectedFile={
                frontImage && frontImage.userImage && frontImage.userImage.file instanceof File ? frontImage.userImage.file
                : null
              }
            />
          </div>
        )}

      </div>
        {imageVariants.length > 0 && (
          <Chooser
            title="Choose a greetings card design"
            subtitle={
              selectedVariant
                ? `${selectedVariant.public_title || selectedVariant.title || 'Design selected'}`.replace('Image Upload / ', '')
                : 'Select a design'
            }
          >
            <div className={styles['chooser--content']}>
              <div className={styles['stock-card--chooser-grid']}>
                {imageVariants.map((variant) => {
                  const variantImg =
                    (variant?.featured_image && variant.featured_image.src) ||
                    variant?.featured_media?.preview_image?.src ||
                    null;
                  const variantImgUrl =
                    variantImg && typeof variantImg === 'string'
                      ? variantImg.startsWith('/' + '/')
                        ? `https:${variantImg}`
                        : variantImg
                      : null;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      className={`${styles['stock-card--chooser-item']} ${
                        selectedVariantId === variant.id ? styles['stock-card--chooser-item--selected'] : ''
                      }`}
                      onClick={() => setSelectedVariantId(variant.id)}
                    >
                      {variantImgUrl ? <img src={variantImgUrl} alt={variant.title || 'Insert design'} /> : null}
                      <p>{`${variant.public_title || variant.title || 'Insert design'}`.replace('Image Upload / ', '')}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </Chooser>
        )}        
    </div>
  );
}
